import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { APP_NAME } from "@/lib/brand";
import { useNavigate } from "react-router-dom";
import {
  X,
  Loader2,
  Download,
  FileDown,
  Share2,
  ExternalLink,
  Copy,
  Trash2,
  Bell,
  BellRing,
  MoreVertical,
  Maximize2,
  Pencil,
  SlidersHorizontal,
  Table2,
  Plus,
  LayoutDashboard,
  Code,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { cn } from "@/lib/utils";
import { toTitleCase } from "@/lib/text-utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import DashboardFilters, { type SavedToolCall, type FilterOverrides, detectFilters } from "./DashboardFilters";
import DashboardFilterBar, { type ActiveFilterValues } from "./DashboardFilterBar";
import FilterSuggestionsDialog, { type ConfiguredFilter, type FilterSuggestionsResult } from "./FilterSuggestionsDialog";
import DashboardSubscribeDialog from "./admin/DashboardSubscribeDialog";
import ShareDialog from "./admin/ShareDialog";
import ViewAsTableDialog from "./ViewAsTableDialog";
import { usePiiMasking } from "@/hooks/usePiiMasking";
import MaskedValue from "@/components/MaskedValue";
import {
  extractPlaceholders,
  inferPlaceholderInfo,
  replacePlaceholders,
  type PlaceholderInfo,
} from "@/lib/query-placeholders";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import PlaceholderInput from "./admin/PlaceholderInput";
import QueryEditor from "./admin/QueryEditor";
import { Label } from "@/components/ui/label";

// ─── Widget types ─────────────────────────────────────────────────────────────

/** Common fields shared by all widget types. */
interface WidgetBase {
  sqlQuery?: string;
  /** Optional display name for the query. */
  _queryName?: string;
}

interface StatWidget extends WidgetBase {
  type: "stat";
  title: string;
  value: string;
  change?: string;
  changeType?: "positive" | "negative" | "neutral";
}

interface ChartWidget extends WidgetBase {
  type: "bar" | "line" | "area";
  title: string;
  data: Record<string, any>[];
  xKey: string;
  yKeys: string[];
  colors?: string[];
}

interface PieWidget extends WidgetBase {
  type: "pie";
  title: string;
  data: { name: string; value: number }[];
  colors?: string[];
}

interface TableWidget extends WidgetBase {
  type: "table";
  title: string;
  columns: string[];
  rows: string[][];
}

type Widget = StatWidget | ChartWidget | PieWidget | TableWidget;

export interface DashboardSpec {
  title: string;
  widgets: Widget[];
  /** MCP tool calls saved when this dashboard was generated (enables refresh). */
  savedToolCalls?: SavedToolCall[];
  /** Original data source IDs used, needed for server resolution on refresh. */
  dataSourceIds?: string[];
  /** Last updated timestamp for display */
  updatedAt?: string;
  /** User-configured filters from the filter suggestions feature */
  configuredFilters?: ConfiguredFilter[];
}

interface DashboardPanelProps {
  dashboard: DashboardSpec | null;
  loading: boolean;
  onClose: () => void;
  isPublic?: boolean;
  onDelete?: () => void;
  snapshotId?: string | null;
  onSaveFilters?: (result: FilterSuggestionsResult) => void;
  onRename?: (newTitle: string) => void;
  onWidgetEdit?: (index: number) => void;
  onWidgetDelete?: (index: number) => void;
  onWidgetClone?: (index: number) => void;
  onAddWidget?: () => void;
  onOpenInDashboardTab?: () => void;
  onDownloadPdf?: () => void;
  /** Called when a widget's query is updated via the inline query editor. */
  onWidgetQueryUpdate?: (widgetIndex: number, sqlQuery: string, queryName?: string) => void;
  /** Called after SQL queries finish with updated widgets, for caching. */
  onCacheUpdate?: (widgets: Widget[]) => void;
  /** When true, widgets already contain cached data — skip auto-fetch on load. */
  hasCachedData?: boolean;
}

const DEFAULT_COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7c43", "#a4de6c", "#d0ed57"];

// ─── Widget kebab menu ────────────────────────────────────────────────────────

interface WidgetMenuProps {
  widget: Widget;
  index: number;
  /** Resolved SQL (inline sqlQuery). */
  resolvedSql?: string;
  onExpand: (index: number) => void;
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
  onClone?: (index: number) => void;
  onViewTable?: (sqlQuery: string, title: string) => void;
  onShowQuery?: (sqlQuery: string, title: string, widgetIndex: number) => void;
}

const WidgetKebabMenu = ({
  widget,
  index,
  resolvedSql,
  onExpand,
  onEdit,
  onDelete,
  onClone,
  onViewTable,
  onShowQuery,
}: WidgetMenuProps) => {
  const sql = resolvedSql || widget.sqlQuery;

  const handleViewTable = () => {
    if (!sql || !onViewTable) return;
    onViewTable(sql, widget.title || "Widget Query");
  };

  const handleShowQuery = () => {
    if (!sql || !onShowQuery) return;
    onShowQuery(sql, widget.title || "Widget Query", index);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 opacity-0 group-hover/widget:opacity-100 transition-opacity"
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-50">
        {sql && onShowQuery && (
          <DropdownMenuItem onClick={handleShowQuery}>
            <Code className="mr-2 h-3.5 w-3.5" /> Edit Query/Filter
          </DropdownMenuItem>
        )}
        {sql && onViewTable && (
          <DropdownMenuItem onClick={handleViewTable}>
            <Table2 className="mr-2 h-3.5 w-3.5" /> View Data
          </DropdownMenuItem>
        )}
        {onEdit && (
          <DropdownMenuItem onClick={() => onEdit(index)}>
            <Pencil className="mr-2 h-3.5 w-3.5" /> Edit Widget
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onExpand(index)}>
          <Maximize2 className="mr-2 h-3.5 w-3.5" /> Expand Widget
        </DropdownMenuItem>
        {onClone && (
          <DropdownMenuItem onClick={() => onClone(index)}>
            <Copy className="mr-2 h-3.5 w-3.5" /> Clone Widget
          </DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(index)}>
            <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete Widget
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ─── Widget card sub-components ───────────────────────────────────────────────

interface PiiUnmaskProps {
  shouldMask: boolean;
  isPiiColumn: (col: string, table?: string) => boolean;
  isUnmasked: (key: string) => boolean;
  requestUnmask: (key: string, fieldName?: string) => Promise<boolean>;
}

const WidgetLoadingBar = () => (
  <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
    <div className="h-full w-1/3 animate-shimmer rounded-full bg-primary/40" />
  </div>
);

const WidgetError = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center gap-1.5 py-2 text-destructive">
    <AlertTriangle className="h-5 w-5" />
    <p className="text-xs text-center max-w-[200px] break-words">{message}</p>
  </div>
);

const WidgetNoData = ({ onShowQueryErrors }: { onShowQueryErrors?: () => void }) => (
  <div className="flex flex-col items-center justify-center gap-1 py-3 text-muted-foreground">
    <p className="text-xs">Query returned 0 rows</p>
    {onShowQueryErrors && (
      <button onClick={onShowQueryErrors} className="text-xs font-medium text-primary hover:underline cursor-pointer">
        Show Query Errors
      </button>
    )}
  </div>
);

const StatCard = ({
  widget,
  menu,
  pii,
  loading,
  error,
  onShowQueryErrors,
}: {
  widget: StatWidget;
  menu?: React.ReactNode;
  pii?: PiiUnmaskProps;
  loading?: boolean;
  error?: string;
  onShowQueryErrors?: () => void;
}) => {
  const isEmpty = !loading && !error && widget.value === "–";
  return (
    <Card className="group/widget">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <p className="text-xs font-medium text-muted-foreground">{widget.title}</p>
          {menu}
        </div>
        {loading ? (
          <div className="mt-3 mb-1">
            <WidgetLoadingBar />
          </div>
        ) : error ? (
          <WidgetError message={error} />
        ) : isEmpty ? (
          <WidgetNoData onShowQueryErrors={onShowQueryErrors} />
        ) : (
          <>
            <p className="mt-1 text-2xl font-bold">{widget.value}</p>
            {widget.change && (
              <p
                className={cn(
                  "mt-1 text-xs font-medium",
                  widget.changeType === "positive"
                    ? "text-green-500"
                    : widget.changeType === "negative"
                      ? "text-red-500"
                      : "text-muted-foreground",
                )}
              >
                {widget.change}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const ChartCard = ({ widget, menu, loading, error, onShowQueryErrors }: { widget: ChartWidget; menu?: React.ReactNode; loading?: boolean; error?: string; onShowQueryErrors?: () => void }) => {
  const colors = widget.colors || DEFAULT_COLORS;
  const ChartComponent = widget.type === "line" ? LineChart : widget.type === "area" ? AreaChart : BarChart;
  const isEmpty = !loading && !error && widget.data.length === 0;

  return (
    <Card className="col-span-1 sm:col-span-2 group/widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{widget.title}</CardTitle>
          {menu}
        </div>
      </CardHeader>
      <CardContent className="h-56">
        {loading ? (
          <div className="flex h-full items-center justify-center px-8">
            <WidgetLoadingBar />
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center">
            <WidgetError message={error} />
          </div>
        ) : isEmpty ? (
          <div className="flex h-full items-center justify-center">
            <WidgetNoData onShowQueryErrors={onShowQueryErrors} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent data={widget.data}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey={widget.xKey} tick={{ fontSize: 11 }} className="text-muted-foreground" />
              <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {widget.yKeys.map((key, i) =>
                widget.type === "bar" ? (
                  <Bar key={key} dataKey={key} fill={colors[i % colors.length]} radius={[4, 4, 0, 0]} />
                ) : widget.type === "area" ? (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[i % colors.length]}
                    fill={colors[i % colors.length]}
                    fillOpacity={0.2}
                  />
                ) : (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[i % colors.length]}
                    strokeWidth={2}
                    dot={false}
                  />
                ),
              )}
            </ChartComponent>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

const PieCard = ({ widget, menu, loading, error, onShowQueryErrors }: { widget: PieWidget; menu?: React.ReactNode; loading?: boolean; error?: string; onShowQueryErrors?: () => void }) => {
  const colors = widget.colors || DEFAULT_COLORS;
  const isEmpty = !loading && !error && widget.data.length === 0;
  return (
    <Card className="col-span-1 sm:col-span-2 group/widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{widget.title}</CardTitle>
          {menu}
        </div>
      </CardHeader>
      <CardContent className="h-56">
        {loading ? (
          <div className="flex h-full items-center justify-center px-8">
            <WidgetLoadingBar />
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center">
            <WidgetError message={error} />
          </div>
        ) : isEmpty ? (
          <div className="flex h-full items-center justify-center">
            <WidgetNoData onShowQueryErrors={onShowQueryErrors} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={widget.data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {widget.data.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

const TableCard = ({
  widget,
  menu,
  pii,
  widgetIndex,
  loading,
  error,
  onShowQueryErrors,
}: {
  widget: TableWidget;
  menu?: React.ReactNode;
  pii?: PiiUnmaskProps;
  widgetIndex?: number;
  loading?: boolean;
  error?: string;
  onShowQueryErrors?: () => void;
}) => {
  const isEmpty = !loading && !error && widget.rows.length === 0;
  return (
    <Card className="col-span-1 sm:col-span-2 group/widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{widget.title}</CardTitle>
          {menu}
        </div>
      </CardHeader>
      <CardContent className="overflow-auto max-h-56">
        {loading ? (
          <div className="py-4 px-8">
            <WidgetLoadingBar />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-6">
            <WidgetError message={error} />
          </div>
        ) : isEmpty ? (
          <div className="flex items-center justify-center py-6">
            <WidgetNoData onShowQueryErrors={onShowQueryErrors} />
          </div>
        ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {widget.columns.map((col) => (
                <th key={col} className="px-2 py-1.5 text-left font-medium text-muted-foreground">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {widget.rows.map((row, i) => (
              <tr key={i} className="border-b border-border last:border-0">
                {row.map((cell, j) => {
                  const colName = widget.columns[j];
                  const isMaskedPii = pii?.shouldMask && colName && pii.isPiiColumn(colName);
                  const unmaskKey = `widget-${widgetIndex ?? 0}-row-${i}-col-${colName}`;
                  return (
                    <td key={j} className="px-2 py-1.5">
                      {isMaskedPii && pii ? (
                        <MaskedValue
                          originalValue={cell}
                          unmaskKey={unmaskKey}
                          fieldName={colName}
                          isUnmasked={pii.isUnmasked(unmaskKey)}
                          onRequestUnmask={pii.requestUnmask}
                        />
                      ) : (
                        cell
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DashboardPanel = ({
  dashboard,
  loading,
  onClose,
  isPublic,
  onDelete,
  snapshotId,
  onSaveFilters,
  onRename,
  onWidgetEdit,
  onWidgetDelete,
  onWidgetClone,
  onAddWidget,
  onOpenInDashboardTab,
  onDownloadPdf,
  onWidgetQueryUpdate,
  onCacheUpdate,
  hasCachedData,
}: DashboardPanelProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile, clientInfo } = useAuth();
  const { shouldMask, maskText, maskRows, maskTableRows, isPiiColumn, isUnmasked, requestUnmask } = usePiiMasking();
  const piiProps: PiiUnmaskProps = { shouldMask, isPiiColumn, isUnmasked, requestUnmask };
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [expandedWidgetIndex, setExpandedWidgetIndex] = useState<number | null>(null);
  const [filterSuggestionsOpen, setFilterSuggestionsOpen] = useState(false);
  const [activeFilterValues, setActiveFilterValues] = useState<ActiveFilterValues>({});
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [viewTableOpen, setViewTableOpen] = useState(false);
  const [viewTableSql, setViewTableSql] = useState("");
  const [viewTableTitle, setViewTableTitle] = useState("");
  const [showQueryWidgetIndex, setShowQueryWidgetIndex] = useState<number | undefined>();
  const [editQueryData, setEditQueryData] = useState<Record<string, any> | null>(null);

  // Live dashboard state (may be overwritten on refresh)
  const [liveDashboard, setLiveDashboard] = useState<DashboardSpec | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Use liveDashboard if set, otherwise fall back to the prop
  const activeDashboard = liveDashboard ?? dashboard;

  // Detect whether this dashboard has refreshable tool calls
  const savedToolCalls = activeDashboard?.savedToolCalls ?? [];
  const { hasStartDate, hasEndDate, dynamicFilters } = detectFilters(savedToolCalls);
  const isRefreshable = savedToolCalls.length > 0 && (hasStartDate || hasEndDate || dynamicFilters.length > 0);

  // ── Data freshness: only auto-fetch on first load, then require manual refresh
  // If cached data is provided, skip initial auto-fetch (user can still press Refresh)
  const hasFetchedRef = useRef(!!hasCachedData);

  // Reset live state when the base dashboard prop changes
  const prevDashboardRef = useRef(dashboard);
  if (prevDashboardRef.current !== dashboard) {
    prevDashboardRef.current = dashboard;
    setLiveDashboard(null);
    hasFetchedRef.current = !!hasCachedData; // skip auto-fetch if cached data present
  }

  // ── Resolve widget SQL ──────────────────────────────────────────────────────
  // SQL is stored inline in each widget's sqlQuery field.
  const [resolvedSqlMap, setResolvedSqlMap] = useState<Record<number, string>>({});
  const [sqlMapVersion, setSqlMapVersion] = useState(0);
  const [loadingWidgets, setLoadingWidgets] = useState<Set<number>>(new Set());
  const [widgetErrors, setWidgetErrors] = useState<Map<number, string>>(new Map());

  const resolveAndSetSqlMap = useCallback(async (widgets: Widget[]) => {
    const map: Record<number, string> = {};
    widgets.forEach((w, i) => {
      if (w.sqlQuery) map[i] = w.sqlQuery;
    });
    setResolvedSqlMap(map);
    return map;
  }, []);

  useEffect(() => {
    if (!dashboard) {
      setResolvedSqlMap({});
      return;
    }
    let cancelled = false;
    resolveAndSetSqlMap(dashboard.widgets)
      .then((map) => {
        if (cancelled) return;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dashboard?.widgets, sqlMapVersion]);

  // ── Dashboard-level placeholder filters ──────────────────────────────────
  const { schemaData } = useMcpSchema();
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});

  // Collect unique placeholders across all resolved widget SQL queries
  const allWidgetSql = useMemo(() => Object.values(resolvedSqlMap).join("\n"), [resolvedSqlMap]);

  const placeholderNames = useMemo(() => extractPlaceholders(allWidgetSql), [allWidgetSql]);

  const placeholderInfos: PlaceholderInfo[] = useMemo(
    () => placeholderNames.map((name) => inferPlaceholderInfo(allWidgetSql, name, schemaData)),
    [placeholderNames, allWidgetSql, schemaData],
  );

  // Seed placeholder values with defaults when the set of placeholders changes
  useEffect(() => {
    setPlaceholderValues((prev) => {
      const next: Record<string, string> = {};
      for (const ph of placeholderInfos) {
        next[ph.name] = prev[ph.name] ?? ph.defaultValue ?? "";
      }
      return next;
    });
  }, [placeholderNames.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SQL query auto-refresh ─────────────────────────────────────────────────
  // When a dashboard is loaded with widgets that have sqlQuery fields, execute
  // them via mcp-api/execute/queries to populate widgets with the latest data.

  /** Parse a raw execute/queries response for one query into an array of row objects. */
  const parseQueryRows = (raw: unknown): Record<string, unknown>[] => {
    if (Array.isArray(raw)) {
      const first = raw[0];
      if (Array.isArray(first)) return first as Record<string, unknown>[];
      if (first && typeof first === "object" && ("rows" in first || "result" in first)) {
        return ((first as any).rows || (first as any).result || []) as Record<string, unknown>[];
      }
      return raw as Record<string, unknown>[];
    }
    if (raw && typeof raw === "object") {
      const r = raw as any;
      if (r.results) {
        const first = Array.isArray(r.results) ? r.results[0] : r.results;
        return Array.isArray(first) ? first : first?.rows || first?.result || [];
      }
      if (r.rows) return r.rows;
    }
    return [];
  };

  /** Return a widget with its data fields cleared (no hardcoded/LLM data). */
  const clearWidgetData = (widget: Widget): Widget => {
    if (widget.type === "stat") return { ...widget, value: "–", change: undefined };
    if (widget.type === "bar" || widget.type === "line" || widget.type === "area") return { ...widget, data: [] };
    if (widget.type === "pie") return { ...widget, data: [] };
    if (widget.type === "table") return { ...widget, columns: [], rows: [] };
    return widget;
  };

  /** Apply fresh SQL query rows to a widget, returning an updated widget. */
  const applyQueryRows = (widget: Widget, rows: Record<string, unknown>[]): Widget => {
    if (rows.length === 0) return clearWidgetData(widget);

    if (widget.type === "stat") {
      // Use the first column of the first row as the value
      const firstRow = rows[0];
      const firstKey = Object.keys(firstRow)[0];
      const rawVal = firstRow["value"] ?? firstRow[firstKey];
      return { ...widget, value: rawVal != null ? String(rawVal) : widget.value };
    }

    if (widget.type === "bar" || widget.type === "line" || widget.type === "area") {
      // Rows map directly to chart data
      return { ...widget, data: rows as Record<string, any>[] };
    }

    if (widget.type === "pie") {
      // Expect rows with "name" and "value" columns (or first two columns)
      const firstRow = rows[0];
      const keys = Object.keys(firstRow);
      const nameKey = "name" in firstRow ? "name" : keys[0];
      const valueKey = "value" in firstRow ? "value" : (keys[1] ?? keys[0]);
      return {
        ...widget,
        data: rows.map((r) => ({
          name: String(r[nameKey] ?? ""),
          value: Number(r[valueKey] ?? 0),
        })),
      };
    }

    if (widget.type === "table") {
      const columns = Object.keys(rows[0]);
      const tableRows = rows.map((r) => columns.map((c) => String(r[c] ?? "")));
      return { ...widget, columns, rows: tableRows };
    }

    return widget;
  };

  const executeSqlQueries = useCallback(
    async (
      dash: DashboardSpec,
      sqlMap: Record<number, string>,
      phValues?: Record<string, string>,
      phInfos?: PlaceholderInfo[],
    ) => {
      const entries = Object.entries(sqlMap).map(([idx, sql]) => ({
        index: Number(idx),
        widget: dash.widgets[Number(idx)],
        sql,
      }));
      if (entries.length === 0) return;

      const indices = new Set(entries.map((e) => e.index));
      setLoadingWidgets(indices);
      // Clear previous errors for widgets being refreshed
      setWidgetErrors((prev) => {
        const next = new Map(prev);
        indices.forEach((i) => next.delete(i));
        return next;
      });

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      // Fire each widget query independently; update UI as each resolves
      const promises = entries.map(async (item) => {
        const sql =
          phValues && phInfos && phInfos.length > 0 ? replacePlaceholders(item.sql, phValues, phInfos) : item.sql;

        try {
          const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
            method: "POST",
            headers,
            body: JSON.stringify([sql]),
          });

          if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            const errMsg = errBody?.error || errBody?.detail || `Query failed (${res.status})`;
            setWidgetErrors((prev) => new Map(prev).set(item.index, errMsg));
            // Clear hardcoded data so stale LLM-generated values are not shown
            setLiveDashboard((prev) => {
              const base = prev ?? dash;
              const widgets = [...base.widgets];
              widgets[item.index] = clearWidgetData(item.widget);
              return { ...base, widgets, updatedAt: new Date().toISOString() };
            });
            setLoadingWidgets((prev) => {
              const next = new Set(prev);
              next.delete(item.index);
              return next;
            });
            return;
          }

          const responseData = await res.json();
          const resultsArray: unknown[] = Array.isArray(responseData) ? responseData : [responseData];
          const rows = parseQueryRows(resultsArray[0]);

          const updatedWidget = applyQueryRows(item.widget, rows);
          setLiveDashboard((prev) => {
            const base = prev ?? dash;
            const widgets = [...base.widgets];
            widgets[item.index] = updatedWidget;
            return { ...base, widgets, updatedAt: new Date().toISOString() };
          });
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : "Query failed";
          setWidgetErrors((prev) => new Map(prev).set(item.index, errMsg));
          // Clear hardcoded data on error
          setLiveDashboard((prev) => {
            const base = prev ?? dash;
            const widgets = [...base.widgets];
            widgets[item.index] = clearWidgetData(item.widget);
            return { ...base, widgets, updatedAt: new Date().toISOString() };
          });
        } finally {
          setLoadingWidgets((prev) => {
            const next = new Set(prev);
            next.delete(item.index);
            return next;
          });
        }
      });

      await Promise.allSettled(promises);
      hasFetchedRef.current = true;

      // Notify parent with updated widgets for caching
      if (onCacheUpdate) {
        setLiveDashboard((prev) => {
          if (prev) onCacheUpdate(prev.widgets);
          return prev;
        });
      }
    },
    [onCacheUpdate],
  );

  // Auto-execute SQL queries only on first load; subsequent refreshes are manual.
  // Depends on both resolvedSqlMap and loading so that queries fire once loading
  // finishes even if resolvedSqlMap was already resolved while loading was true.
  useEffect(() => {
    if (!activeDashboard || loading || Object.keys(resolvedSqlMap).length === 0) return;
    if (hasFetchedRef.current) return; // already loaded, user must press Refresh Data
    executeSqlQueries(activeDashboard, resolvedSqlMap, placeholderValues, placeholderInfos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedSqlMap, loading]);

  // Manual refresh handler for the Refresh Data button
  const handleManualRefresh = useCallback(() => {
    if (!activeDashboard || Object.keys(resolvedSqlMap).length === 0) return;
    executeSqlQueries(activeDashboard, resolvedSqlMap, placeholderValues, placeholderInfos);
  }, [activeDashboard, resolvedSqlMap, placeholderValues, placeholderInfos, executeSqlQueries]);

  // Apply placeholder filters manually
  const handleApplyFilters = useCallback(() => {
    if (!activeDashboard || Object.keys(resolvedSqlMap).length === 0) return;
    executeSqlQueries(activeDashboard, resolvedSqlMap, placeholderValues, placeholderInfos);
  }, [activeDashboard, resolvedSqlMap, placeholderValues, placeholderInfos, executeSqlQueries]);

  // ── Refresh handler ──────────────────────────────────────────────────────

  const handleRefresh = async (overrides: FilterOverrides) => {
    if (!activeDashboard || savedToolCalls.length === 0) return;
    setRefreshing(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${supabaseUrl}/functions/v1/refresh-dashboard`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          toolCalls: savedToolCalls,
          filterOverrides: overrides,
          dataSourceIds: activeDashboard.dataSourceIds,
          existingTitle: activeDashboard.title,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to refresh" }));
        throw new Error(err.error || "Failed to refresh dashboard");
      }

      const { _refreshed, ...spec } = await res.json();
      setLiveDashboard({
        ...spec,
        savedToolCalls,
        dataSourceIds: activeDashboard.dataSourceIds,
      });
    } catch (err: any) {
      console.error("Refresh error:", err);
      toast({ title: "Refresh failed", description: err.message, variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  // ── Share handler ──────────────────────────────────────────────────────

  const handleShare = async () => {
    if (!activeDashboard) return;
    setSharing(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("shared_dashboards")
        .insert({
          user_id: user.id,
          client_id: profile?.client_id ?? "",
          title: activeDashboard.title,
          spec: { widgets: activeDashboard.widgets } as any,
        })
        .select("id")
        .single();

      if (error) throw error;

      const url = `${window.location.origin}/shared/${data.id}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied!", description: "Public dashboard link copied to clipboard" });
    } catch (err: any) {
      console.error("Share error:", err);
      toast({ title: "Error", description: err.message || "Failed to share", variant: "destructive" });
    } finally {
      setSharing(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    toast({ title: "Copied!", description: "Link copied to clipboard" });
  };

  return (
    <div data-dashboard-panel className={cn("flex h-full w-full flex-col bg-background", !isPublic && "border-l border-border")}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-b border-border bg-muted/60 px-4 py-4">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1">
            {editingTitle ? (
              <input
                autoFocus
                className="text-base font-bold bg-background border border-input rounded px-1.5 py-0.5"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const trimmed = titleDraft.trim();
                    if (trimmed && onRename) onRename(trimmed);
                    setEditingTitle(false);
                  }
                  if (e.key === "Escape") setEditingTitle(false);
                }}
                onBlur={() => {
                  const trimmed = titleDraft.trim();
                  if (trimmed && onRename) onRename(trimmed);
                  setEditingTitle(false);
                }}
              />
            ) : (
              <h2 className="text-3xl font-black tracking-tight">
                {loading ? "Generating Dashboard…" : toTitleCase(activeDashboard?.title || "Dashboard")}
              </h2>
            )}
            {!loading && activeDashboard && !isPublic && (
              <div className="flex items-center gap-1 ml-2">
                {onOpenInDashboardTab && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Open in Dashboards tab"
                    onClick={onOpenInDashboardTab}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
                {onDownloadPdf && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Download as PDF"
                    onClick={onDownloadPdf}
                  >
                    <FileDown className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!loading && activeDashboard && Object.keys(resolvedSqlMap).length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleManualRefresh}
              disabled={loadingWidgets.size > 0}
              className="flex flex-col items-center gap-0 h-auto py-1 px-3"
            >
              <span className="flex items-center">
                <RefreshCw className={cn("mr-1 h-3.5 w-3.5", loadingWidgets.size > 0 && "animate-spin")} />
                Refresh Data
              </span>
              {activeDashboard.updatedAt && (
                <span className="text-[9px] text-muted-foreground font-normal">
                  {format(new Date(activeDashboard.updatedAt), "MMM d, h:mm a")}
                </span>
              )}
            </Button>
          )}
          {!loading && activeDashboard && !isPublic && onSaveFilters && (
            <Button size="sm" variant="outline" onClick={() => setFilterSuggestionsOpen(true)}>
              <SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Suggest Filters
            </Button>
          )}
          {!loading && activeDashboard && !isPublic && onAddWidget && (
            <Button size="sm" variant="outline" onClick={onAddWidget}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Widget
            </Button>
          )}
        </div>
      </div>

      {/* ── Share URL bar ────────────────────────────────────────────────── */}
      {shareUrl && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-2">
          <input
            readOnly
            value={shareUrl}
            className="flex-1 rounded-md bg-background border border-input px-2 py-1 text-xs"
          />
          <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={copyShareUrl}>
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* ── Filter bar (only for refreshable dashboards) ─────────────────── */}
      {!loading && isRefreshable && !isPublic && (
        <DashboardFilters toolCalls={savedToolCalls} onRefresh={handleRefresh} refreshing={refreshing} />
      )}

      {/* ── Configured filter bar (from filter suggestions) ─────────────── */}
      {!loading &&
        !isPublic &&
        activeDashboard?.configuredFilters &&
        activeDashboard.configuredFilters.length > 0 &&
        snapshotId && (
          <DashboardFilterBar
            filters={activeDashboard.configuredFilters}
            dashboardId={snapshotId}
            onChange={setActiveFilterValues}
          />
        )}

      {/* ── Placeholder filters (from widget SQL {{placeholders}}) ──────── */}
      {!loading && placeholderInfos.length > 0 && (
        <div className="flex flex-wrap items-end gap-3 border-b border-border bg-muted/30 px-4 py-3">
          {placeholderInfos.map((ph) => (
            <div key={ph.name} className="flex flex-col gap-1">
              <Label className="text-[10px] text-muted-foreground">{ph.name}</Label>
              <PlaceholderInput
                placeholder={ph}
                value={placeholderValues[ph.name] ?? ""}
                onChange={(v) => setPlaceholderValues((prev) => ({ ...prev, [ph.name]: v }))}
                sql={allWidgetSql}
              />
            </div>
          ))}
          <Button size="sm" onClick={handleApplyFilters} disabled={loadingWidgets.size > 0}>
            {loadingWidgets.size > 0 && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Apply Filters
          </Button>
        </div>
      )}

      {/* ── Widget grid ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4" ref={contentRef}>
        {loading || refreshing ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : activeDashboard ? (
          activeDashboard.widgets.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-muted-foreground">
              <LayoutDashboard className="h-10 w-10" />
              <p className="text-sm font-medium">No widgets added !!</p>
              {onAddWidget && (
                <Button size="sm" variant="outline" onClick={onAddWidget}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add Widget
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activeDashboard.widgets.map((widget, i) => {
                const menu = (
                  <WidgetKebabMenu
                    widget={widget}
                    index={i}
                    resolvedSql={resolvedSqlMap[i]}
                    onExpand={setExpandedWidgetIndex}
                    onEdit={onWidgetEdit}
                    onDelete={onWidgetDelete}
                    onClone={onWidgetClone}
                    onViewTable={(sql, widgetTitle) => {
                      setViewTableSql(sql);
                      setViewTableTitle(widgetTitle);
                      setViewTableOpen(true);
                    }}
                    onShowQuery={(sql, widgetTitle, widgetIndex) => {
                      setShowQueryWidgetIndex(widgetIndex);
                      setEditQueryData({
                        sql_query: sql,
                        name: widgetTitle || "",
                        generation_mode: "manual",
                      });
                    }}
                  />
                );

                // For table/stat: pass original data + PII props for click-to-unmask
                // For charts/pie: pre-mask since Recharts only accepts primitive values
                const maskedWidget = shouldMask
                  ? (() => {
                      if (widget.type === "pie") {
                        return {
                          ...widget,
                          data: widget.data.map((d) => ({
                            ...d,
                            name: isPiiColumn(d.name) ? "●●●●●●" : d.name,
                          })),
                        } as PieWidget;
                      }
                      if (widget.type === "bar" || widget.type === "line" || widget.type === "area") {
                        return {
                          ...widget,
                          data: maskRows(widget.data),
                        } as ChartWidget;
                      }
                      return widget;
                    })()
                  : widget;

                const isWidgetLoading = loadingWidgets.has(i);
                const widgetError = widgetErrors.get(i);

                const showQueryErrors = resolvedSqlMap[i]
                  ? () => {
                      setShowQueryWidgetIndex(i);
                      setEditQueryData({
                        sql_query: resolvedSqlMap[i],
                        name: widget.title || "",
                        generation_mode: "manual",
                      });
                    }
                  : undefined;

                switch (maskedWidget.type) {
                  case "stat":
                    return (
                      <StatCard
                        key={i}
                        widget={maskedWidget}
                        menu={menu}
                        pii={shouldMask ? piiProps : undefined}
                        loading={isWidgetLoading}
                        error={widgetError}
                        onShowQueryErrors={showQueryErrors}
                      />
                    );
                  case "bar":
                  case "line":
                  case "area":
                    return (
                      <ChartCard key={i} widget={maskedWidget as ChartWidget} menu={menu} loading={isWidgetLoading} error={widgetError} onShowQueryErrors={showQueryErrors} />
                    );
                  case "pie":
                    return <PieCard key={i} widget={maskedWidget as PieWidget} menu={menu} loading={isWidgetLoading} error={widgetError} onShowQueryErrors={showQueryErrors} />;
                  case "table":
                    return (
                      <TableCard
                        key={i}
                        widget={maskedWidget as TableWidget}
                        menu={menu}
                        pii={shouldMask ? piiProps : undefined}
                        widgetIndex={i}
                        loading={isWidgetLoading}
                        error={widgetError}
                        onShowQueryErrors={showQueryErrors}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </div>
          )
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No dashboard data</div>
        )}
      </div>

      {/* ── Expand widget dialog ──────────────────────────────────────────── */}
      {expandedWidgetIndex !== null &&
        activeDashboard &&
        activeDashboard.widgets[expandedWidgetIndex] &&
        (() => {
          const origWidget = activeDashboard.widgets[expandedWidgetIndex];
          const w = shouldMask
            ? (() => {
                if (origWidget.type === "pie")
                  return {
                    ...origWidget,
                    data: origWidget.data.map((d: any) => ({ ...d, name: isPiiColumn(d.name) ? "●●●●●●" : d.name })),
                  } as PieWidget;
                if (origWidget.type === "bar" || origWidget.type === "line" || origWidget.type === "area")
                  return { ...origWidget, data: maskRows(origWidget.data) } as ChartWidget;
                return origWidget;
              })()
            : origWidget;

          return (
            <Dialog open={true} onOpenChange={() => setExpandedWidgetIndex(null)}>
              <DialogContent className="sm:max-w-4xl max-h-[80vh]">
                <DialogHeader>
                  <DialogTitle>{w.title}</DialogTitle>
                </DialogHeader>
                <div className="min-h-[400px]">
                  {(() => {
                    switch (w.type) {
                      case "stat":
                        return <StatCard widget={w} pii={shouldMask ? piiProps : undefined} />;
                      case "bar":
                      case "line":
                      case "area":
                        return <ChartCard widget={w as ChartWidget} />;
                      case "pie":
                        return <PieCard widget={w as PieWidget} />;
                      case "table":
                        return (
                          <TableCard
                            widget={w as TableWidget}
                            pii={shouldMask ? piiProps : undefined}
                            widgetIndex={expandedWidgetIndex + 1000}
                          />
                        );
                      default:
                        return null;
                    }
                  })()}
                </div>
              </DialogContent>
            </Dialog>
          );
        })()}

      {snapshotId && (
        <DashboardSubscribeDialog
          open={subscribeOpen}
          onOpenChange={setSubscribeOpen}
          dashboardSnapshotId={snapshotId}
          dashboardTitle={activeDashboard?.title || "Dashboard"}
        />
      )}

      {onSaveFilters && activeDashboard && (
        <FilterSuggestionsDialog
          open={filterSuggestionsOpen}
          onOpenChange={setFilterSuggestionsOpen}
          widgets={activeDashboard.widgets.map((w: any) => ({
            sqlQuery: w.sqlQuery,
            _queryName: w._queryName,
          }))}
          existingFilters={activeDashboard.configuredFilters || []}
          onSave={(result) => {
            // Apply updated widget SQL to local state
            const updatedWidgets = activeDashboard.widgets.map((w, i) => {
              const updated = result.updatedWidgets[i];
              if (updated && updated.sqlQuery !== w.sqlQuery) {
                return { ...w, sqlQuery: updated.sqlQuery };
              }
              return w;
            });
            setLiveDashboard({
              ...activeDashboard,
              widgets: updatedWidgets,
              configuredFilters: result.filters,
            });
            setSqlMapVersion((v) => v + 1);
            onSaveFilters(result);
          }}
        />
      )}

      {snapshotId && (
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          resourceType="dashboard"
          resourceId={snapshotId}
          resourceTitle={activeDashboard?.title || "Dashboard"}
        />
      )}

      <ViewAsTableDialog
        open={viewTableOpen}
        onOpenChange={setViewTableOpen}
        sqlQuery={viewTableSql}
        title={viewTableTitle}
      />

      {/* ── Inline Query Editor dialog ─────────────────────────────────── */}
      <Dialog
        open={!!editQueryData}
        onOpenChange={(open) => {
          if (!open) {
            setEditQueryData(null);
            setSqlMapVersion((v) => v + 1);
          }
        }}
      >
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col">
          {editQueryData && (
            <QueryEditor
              editingQuery={editQueryData}
              onClose={() => {
                setEditQueryData(null);
                setSqlMapVersion((v) => v + 1);
              }}
              saveToDb={false}
              onSaved={(query) => {
                // Write the updated SQL back into the widget
                if (showQueryWidgetIndex !== undefined && activeDashboard) {
                  const updatedWidgets = activeDashboard.widgets.map((w, i) =>
                    i === showQueryWidgetIndex
                      ? { ...w, sqlQuery: query.sql_query, _queryName: query.name || undefined }
                      : w
                  );
                  const updatedDashboard = { ...activeDashboard, widgets: updatedWidgets };
                  setLiveDashboard(updatedDashboard);
                  // Persist updated widget query to the dashboard JSON in DB
                  if (onWidgetQueryUpdate) {
                    onWidgetQueryUpdate(showQueryWidgetIndex, query.sql_query, query.name || undefined);
                  }
                  // Auto-refresh the widget with the new query
                  if (query.sql_query) {
                    executeSqlQueries(
                      updatedDashboard,
                      { [showQueryWidgetIndex]: query.sql_query },
                      placeholderValues,
                      placeholderInfos,
                    );
                  }
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPanel;
